import { ROLES } from "../../utils/constants";
import { OPS_ROLES, PHARMACY_ROLES, INVENTORY_ROLES, SALES_ROLES, FINANCE_ROLES, LOGISTICS_ROLES } from "./roles";




/** Where a user with no dashboard at all is sent. Registered for every role. */
export const NO_ACCESS_PATH = "/welcome";

const LEGACY_LANDING_BY_ROLE = [
    { roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN], path: "/home" },
    { roles: [ROLES.PHARMACIST, ROLES.SUPER_PHARMACIST], path: "/all-orders" },
    { roles: [ROLES.SUPPORT, ROLES.SUPER_SUPPORT, ROLES.OPS_SUPPORT, ROLES.SUPER_OPS_SUPPORT], path: "/request-callbacks" },
    { roles: [ROLES.MARKETING], path: "/marketing" },
    { roles: [ROLES.SUPER_FINANCE], path: "/payments" },
    { roles: [ROLES.SUPER_LOGISTICS], path: "/delivery" },
];

// EVERY path above must be one its roles are granted in ROUTER_ROLES_MAP
// (src/config/constants/route.js). Legacy registers routes by OMISSION, so a landing the
// role does not hold renders NoPageFound -- a Legacy tab that opens nothing, with no error
// to explain it. That was live for OPS_SUPPORT and SUPER_PHARMACIST until 2026-09-24;
// consoles.test.js now pins the pairing.

export function legacyLandingFor(userRoles = []) {
    const match = LEGACY_LANDING_BY_ROLE.find((entry) => entry.roles.some((role) => userRoles.includes(role)));
    return match ? match.path : NO_ACCESS_PATH;
}


export const CONSOLES = [
    { id: "ops", label: "Ops Console", basePath: "/v2/ops", landing: "/v2/ops/overview", roles: OPS_ROLES },
    { id: "pharmacy", label: "Pharmacy Console", basePath: "/v2/pharmacy", landing: "/v2/pharmacy/orders", roles: PHARMACY_ROLES },
    { id: "sales", label: "Sales Console", basePath: "/v2/sales", landing: "/v2/sales/orders", roles: SALES_ROLES },
    // Finance Console (`.scratch/finance-dashboard/IMPLEMENTATION.md`). Placed after
    // pharmacy deliberately: this list doubles as default precedence, and finance is a
    // reconciliation surface, not anyone's work queue -- nobody should LAND here.
    //
    // Gated on FINANCE_ROLES since 2026-09-24. It previously borrowed PHARMACY_ROLES,
    // which showed every pharmacist a Finance tab they had no business seeing.
    { id: "finance", label: "Finance Console", basePath: "/v2/finance", landing: "/v2/finance/orders", roles: FINANCE_ROLES },
    // Logistics Console (`.scratch/logistics-dashboard/IMPLEMENTATION.md`). A restyle of the
    // legacy /delivery page; that page stays live, so this is an addition, not a cutover.
    // Gated on LOGISTICS_ROLES — see the note on finance above.
    { id: "logistics", label: "Logistics Console", basePath: "/v2/logistics", landing: "/v2/logistics/deliveries", roles: LOGISTICS_ROLES },
    // `roles: null` -- legacy has no role list of its own; entitlement is decided by
    // whether legacyLandingFor() finds this user a real page (see entitledConsoles).
    // Promoted out of the Pharmacy Console 2026-09-30 -- see src/v2/inventory/nav-config.js.
    { id: "inventory", label: "Inventory Console", basePath: "/v2/inventory", landing: "/v2/inventory/procurement", roles: INVENTORY_ROLES },
    { id: "legacy", label: "Legacy Dashboard", basePath: "/", landing: null, roles: null },
];

const LEGACY_ID = "legacy";

/**
 * Every console this user can reach, in registry order.
 *
 * Legacy is entitled CONDITIONALLY -- only when it has a real page for this role.
 * Without that check KAM would be offered a "Legacy Dashboard" tab that leads straight
 * to the no-access stub, since KAM has no legacy routes at all.
 */
export function entitledConsoles(userRoles = []) {
    return CONSOLES.filter((entry) => {
        if (entry.id === LEGACY_ID) return legacyLandingFor(userRoles) !== NO_ACCESS_PATH;
        return entry.roles.some((role) => userRoles.includes(role));
    });
}

/** The console a user lands on absent a stored preference, or null if entitled to none. */
export function defaultConsoleId(userRoles = []) {
    const entitled = entitledConsoles(userRoles);
    return entitled.length ? entitled[0].id : null;
}

/**
 * Where `consoleId` starts for this user. Falls through to NO_ACCESS_PATH for an
 * unknown id and for `null` -- which is what defaultConsoleId returns when a user is
 * entitled to nothing, so `landingPathFor(defaultConsoleId(roles), roles)` is always
 * safe to call without a null check.
 */
export function landingPathFor(consoleId, userRoles = []) {
    if (consoleId === LEGACY_ID) return legacyLandingFor(userRoles);
    const entry = CONSOLES.find((candidate) => candidate.id === consoleId);
    return entry?.landing || NO_ACCESS_PATH;
}

/**
 * Which console a pathname belongs to. Legacy is the fallback rather than a match,
 * because its basePath is "/" and would otherwise swallow every v2 path too.
 */
export function consoleForPath(pathname = "") {
    const match = CONSOLES.find(
        (entry) => entry.id !== LEGACY_ID && (pathname === entry.basePath || pathname.startsWith(`${entry.basePath}/`))
    );
    return match ? match.id : LEGACY_ID;
}
